import moment from 'moment';

/**
 * 验证邮箱是否正确
 * @param value
 */
export function isEmail(value: string) : boolean {
  return /^[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/.test(value);
}

export function isPasswordFormatCorrect(password: string): boolean {
  return /^(?![0-9]+$)(?![a-zA-Z]+$)[0-9A-Za-z]{8,32}$/.test(password);
}

/**
 * 验证值是否为空，undefined，null，空字符串以及只有空格和换行符都算空
 * @param value
 */
export function isValueEmpty(value: any): boolean {
  return value === undefined || value === null || (typeof value === 'string' && !value.trim());
}

/**
 * 10000 => "10,000"
 * @param {number} num
 */
export function toThousandFilter(num: string | number): string {
  if (isValueEmpty(num)) {
    return '';
  }
  return (+num || 0).toString().replace(/^-?\d+/g, (m) => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','));
}

/**
 * "2021-05-12T08:16:40.400Z" => "2021/05/12 16:16:40"
 * @param {string} time
 */
export function toTimeFormatFilter(time: string): string {
  if (time === '' || time === null || time === undefined) {
    return '';
  }
  return moment.utc(time).local().format('YYYY/MM/DD HH:mm:ss');
}

/**
 * "2021-05-12T08:16:40.400Z" => "2021/05/12"
 * @param {string} time
 */
export function toDateFormatFilter(time: string): string {
  if (time === '' || time === null || time === undefined) {
    return '';
  }
  return moment.utc(time).local().format('YYYY/MM/DD');
}

/**
 * "2021-05-12T08:16:40.400Z" => "16:16:40"
 * @param {string} time
 */
export function toTimeHMSFormatFilter(time: string): string {
  if (time === '' || time === null || time === undefined) {
    return '';
  }
  return moment.utc(time).local().format('HH:mm:ss');
}

/**
 * "2021-05-12T08:16:40.400Z" => "16:16:40"
 * @param {string} time
 */
export function toTimeHMFormatFilter(time: string): string {
  if (time === '' || time === null || time === undefined) {
    return '';
  }
  return moment.utc(time).local().format('HH:mm');
}

/**
 * 1.10 => 1.1
 * @param {number} number
 * @param {number} digit
 */
export function toNumberFormatFilter(number, digit = 2) {
  if (/^([1-9]\d*|[0])$/.test(String(number))) { // 0 或者是正整数
    return number;
  }
  return Number.parseFloat(Number(number).toFixed(digit));
}

/**
 * 1 -> true 1.1 0 -1 -> false
 * @param value
 */
export function isPositiveIntegerNum(value) {
  return isNum(value) && value > 0 && Number.parseInt(value, 10) === Number.parseFloat(value);
}

/**
 * 1 => true
 * @param data
 */
export function isNum(data: any) {
  if (data === '' || data === null) {
    return false;
  }
  const numData = Number(data);
  return !Number.isNaN(numData);
}

/**
 * 1 => true, 1.1 => false
 * @param number
 */
export function isInteger(number: string) {
  return parseInt(number, 10) === Number.parseFloat(number);
}

/**
 * 1 => true, 0 => false, -1 => false
 * @param numString
 */
export function isPositiveNum(numString: string | number) {
  return Number(numString) > 0;
}

/**
 * 防抖
 * @param func
 * @param wait
 * @param immediate
 */
/* eslint-disable */
export function debounce(func, wait, immediate?: boolean) {
  let timeout, args, context, timestamp, result;

  const later = function () {
    // 据上一次触发时间间隔
    const last = +new Date() - timestamp;

    // 上次被包装函数被调用时间间隔 last 小于设定时间间隔 wait
    if (last < wait && last > 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      // 如果设定为immediate===true，因为开始边界已经调用过了此处无需调用
      if (!immediate) {
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      }
    }
  };

  return function (...args) {
    context = this;
    timestamp = +new Date();
    const callNow = immediate && !timeout;
    // 如果延时不存在，重新设定延时
    if (!timeout) timeout = setTimeout(later, wait);
    if (callNow) {
      result = func.apply(context, args);
      context = args = null;
    }

    return result;
  };
}

/**
 * 节流
 * @param fn
 * @param wait
 */
/* eslint-disable */
export function throttle(fn, wait) {
  let timer, previous, now, diff;
  return function () {
    let _args = [].slice.call(arguments);
    let context = this;
    now = Date.now();

    let _fn = function () {
      previous = Date.now();
      timer = null;
      fn.apply(context, _args);
    };

    clearTimeout(timer);

    if (previous !== undefined) {
      diff = now - previous;
      if (diff >= wait) {
        fn.apply(context, _args);
        previous = now;
      } else {
        timer = setTimeout(_fn, wait);
      }
    } else {
      _fn();
    }
  };
}

/**
 * 判断日期是否合法
 * @param date
 */
export function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * 转二进制数组
 * @param rangeInfo
 */
export function toRangeBinary(rangeInfo) {
  const binaryVal = Number(rangeInfo).toString(2);
  const maskVal = [];
  let length = binaryVal.length;
  for (let i = 0; i < length; i++) {
    if (binaryVal.charAt(i) === '1') {
      maskVal.push(length - i);
    }
  }
  return maskVal.reverse();
}

/**
 * 1800 * 1000 -> 00:30
 */
export function formatTime(time) {
  const hour = Math.floor(time / 1000 / 60 / 60);
  const min = Math.floor((time - hour * 1000 * 60 * 60) / 1000 / 60);
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * 获取url里面的文件名字
 * @param url
 * @param key
 * @returns
 */
export function findUrlName(url: string, key: string) {
  const str = decodeURIComponent(url);
  const indexOf = str.indexOf(key);
  if (indexOf === -1) {
    return str;
  }
  return str.substring(indexOf + key.length);
}
